import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import { Button } from "@/components/UI/Button";
import InfoTooltip from "@/components/UI/InfoTooltip";
import featureFlagStore from "@/store/featureFlagStore";
import useTableUIStore from "@/store/tableUIStore";
import { updateTradeExpiry } from "@/store/tradeExpiryACTIONS";
import { ExpiryDurationInput } from "../../../../ActionSummaryOP/selectPanel/ExpiryDurationInput";
import { SettingDropdown } from "../../../../ActionSummaryOP/selectPanel/SettingDropdown";
import { isExpiryTypeOptionDisabled } from "../../../../ActionSummaryOP/selectPanel/CPSettingsGridHelper";
import { getFontSizeClass } from "../../../actionSummaryHelper";
import {
  EXPIRY_POPOVER_WIDTH,
  EXPIRY_TYPE_TIME,
  IAnchorRect,
  IExpiryEditState,
  buildExpiryTypeOptions,
  buildExpiryUpdatePayload,
  calculateExpiryPopoverPosition,
  canSubmitExpiryEdit,
  deriveInitialExpiryState,
} from "./expiryEditHelpers";

// LQC-2172 - the OP's edit-expiry popover, opened by the pencil in the Expires
// column. Anchored where the pencil is and dismissed like a right-click menu
// (outside click / Escape / any scroll) rather than a modal: outside Settings
// the app doesn't use modals.
//
// It replicates the New trade panel's Expiry / Time to Expiry pair by reusing
// those exact controls, but holds its own draft state - writing trade-level
// settings here would leak into the New/Edit panel for the same trade.
const ExpiryEditPopover = ({
  esid,
  expiryEnd,
  anchorRect,
  onClose,
}: {
  esid: string;
  expiryEnd: string | number | Date | null | undefined;
  anchorRect: IAnchorRect;
  onClose: () => void;
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const fontSizeSetting = useTableUIStore((state) => state.fontSize);
  const fontSizeClass = getFontSizeClass(fontSizeSetting);
  // Same flags that gate the options in the New trade panel (LQC-2170).
  const expiryTypeDay = featureFlagStore((state) => state.expiryTypeDay);
  const expiryTypeTime = featureFlagStore((state) => state.expiryTypeTime);

  // Snapshot on open - this is what Update compares against.
  const [initialState] = useState<IExpiryEditState>(() =>
    deriveInitialExpiryState(expiryEnd, Date.now()),
  );
  const [expiryType, setExpiryType] = useState(initialState.expiryType);
  const [expiryTimeValue, setExpiryTimeValue] = useState(
    initialState.expiryTimeValue,
  );
  const [submitting, setSubmitting] = useState(false);

  const popoverRef = useRef<HTMLDivElement>(null);
  // A ref, not state: the blur sets it mid-click, before any re-render lands.
  const durationRejectedRef = useRef(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!popoverRef.current?.contains(event.target as Node)) onClose();
    };
    // Capture phase: the grid body scrolls in its own container, so a
    // bubbling window listener never sees it and the popover would detach
    // from its row.
    const onScroll = () => onClose();

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [onClose]);

  const position = calculateExpiryPopoverPosition(anchorRect, {
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const currentState: IExpiryEditState = { expiryType, expiryTimeValue };
  const canSubmit = canSubmitExpiryEdit(currentState) && !submitting;

  // Stays open on failure - closing would discard a duration the user typed
  // against the 30s..7d validator. The refreshed trade arrives over MegaPipe.
  const handleUpdate = async () => {
    // Pressing Update blurs the duration input, so an invalid value is rejected
    // and reverted by this very click. Swallow it: sending the reverted value
    // while the warning is on screen reads as the typed one being accepted.
    if (durationRejectedRef.current) {
      durationRejectedRef.current = false;
      return;
    }
    setSubmitting(true);
    const { success } = await updateTradeExpiry(
      buildExpiryUpdatePayload(esid, currentState),
      getAccessTokenSilently,
    );
    setSubmitting(false);
    if (!success) return;
    toast.success(`Expiry updated for ${esid}`);
    onClose();
  };

  return createPortal(
    <div
      ref={popoverRef}
      data-testid="ExpiryEditPopover"
      id="ExpiryEditPopover"
      role="dialog"
      aria-label="Edit trade expiry"
      className="fixed z-[9999] rounded-md border border-gray-300 bg-cubeBase-500 text-black shadow-lg dark:border-gray-600 dark:bg-cubeBaseDark-500 dark:text-white"
      style={{
        top: position.top,
        left: position.left,
        width: EXPIRY_POPOVER_WIDTH,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={clsx(
          "border-b border-gray-300 px-3 py-1 uppercase tracking-wider text-gray-500 dark:border-gray-600 dark:text-gray-400",
          fontSizeClass,
        )}
      >
        Edit Expiry - {esid}
      </div>

      <div className="flex flex-col gap-3 px-3 py-3">
        {/* pb-4 reserves the row for a rejection message, so showing one never
            resizes the popover or shifts the buttons. */}
        <div className="flex flex-row gap-3 pb-4">
          <div
            data-testid="ExpiryEditPopoverTypeColumn"
            id="ExpiryEditPopoverTypeColumn"
            className="w-[110px]"
          >
            <div
              className={clsx(
                "mb-1 flex items-center whitespace-nowrap tracking-wider text-gray-500 dark:text-gray-400",
                fontSizeClass,
              )}
            >
              Expiry
              <InfoTooltip
                id={`expiryEditPopoverType-${esid}-info`}
                message={
                  <div className="flex flex-col gap-1">
                    <div>GTC: Good til cancelled</div>
                    <div>Day: Midnight tonight</div>
                    <div>
                      Time: Specify duration matching the 'Time to Expiry'
                      format
                    </div>
                  </div>
                }
                maxWidthClass="max-w-[360px]"
              />
            </div>
            <SettingDropdown
              settingArray={buildExpiryTypeOptions(expiryType)}
              esid={esid}
              settingField="expiryType"
              fontSizeClass={fontSizeClass}
              onSelect={(selected) => setExpiryType(selected.name)}
              isOptionDisabled={(optionName) =>
                isExpiryTypeOptionDisabled(optionName, {
                  expiryTypeDay,
                  expiryTypeTime,
                })
              }
            />
          </div>

          <div
            data-testid="ExpiryEditPopoverTimeColumn"
            id="ExpiryEditPopoverTimeColumn"
            className="w-[150px]"
          >
            <div
              className={clsx(
                "mb-1 whitespace-nowrap tracking-wider text-gray-500 dark:text-gray-400",
                fontSizeClass,
              )}
            >
              Time to Expiry
            </div>
            <ExpiryDurationInput
              esid={esid}
              idSuffix="-expiryEditPopover"
              valueSeconds={expiryTimeValue}
              disabled={expiryType !== EXPIRY_TYPE_TIME}
              fontSizeClass={fontSizeClass}
              onCommit={(seconds) => setExpiryTimeValue(seconds)}
              onRejectionChange={(rejected) => {
                durationRejectedRef.current = rejected;
              }}
              rejectionPlacement="below"
            />
          </div>
        </div>

        <div className="flex flex-row justify-end gap-2">
          <Button
            variant="secondaryBlueAction"
            size="button_sm_medium"
            id="ExpiryEditPopoverCancelButton"
            data-testid="ExpiryEditPopoverCancelButton"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant={canSubmit ? "mainBlueAction" : undefined}
            variantToggle={canSubmit ? undefined : "selectionDisabled"}
            size="button_sm_medium"
            id="ExpiryEditPopoverUpdateButton"
            data-testid="ExpiryEditPopoverUpdateButton"
            disabled={!canSubmit}
            onClick={handleUpdate}
          >
            Update
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ExpiryEditPopover;
